import Carousel from 'components/Carousel'
import React, { useCallback, useRef } from 'react'
import { animateScroll } from 'react-scroll'

import { TrackingPromotionCreative } from '@aboutyou/legacy-tracking-sdk'
import { useDisplayBrowserState } from 'helpers/hooks/useDisplayBrowserState'
import { ElementTrackingContextProvider } from 'helpers/tracking/ElementTrackingContext'
import { SectionTrackingContextProvider } from 'helpers/tracking/SectionTrackingContext'
import { GenericPromotionTracker } from 'helpers/tracking/trackerComponents/ElementTracker'
import { TrackBASMonitoring } from 'helpers/tracking/trackerComponents/TrackBasMonitoring'
import { BASPlacement } from 'services/providers/iris/entities/placementCampaigns/normalizers/types'
import { useBrandOverviewPageStore } from 'store/context'
import styled from 'styled-components'
import {
  mediaDesktopAndUp,
  mediaExtraLargeScreens,
  mediaMaxLBreakpoint,
  mediaMaxMBreakpoint,
  mediaTabletAndDown,
} from 'uiComponents/constants/customMedia'
import DiscoverMore from '../DiscoverMore'
import Slide from './components/Slide'

const SlideContainer = styled.div`
  height: 100%;
  position: relative;
`

const SlideImage: React.FC<{
  placement: BASPlacement
  campaignName: string
  index: number
}> = ({ placement, campaignName, index }) => {
  return (
    <ElementTrackingContextProvider
      key={placement.desktopHash}
      indexInSection={index}
    >
      <GenericPromotionTracker
        id={placement.id}
        name={campaignName}
        url={placement.target?.url}
        source={TrackingPromotionCreative.BasBrandOverviewSlider}
        cachePageImpression
        locationId={TrackingPromotionCreative.BasBrandOverviewSlider}
      >
        <SlideContainer>
          <Slide
            image={{
              hash: placement.desktopHash,
              url: placement.target?.url,
            }}
          />
        </SlideContainer>
      </GenericPromotionTracker>
    </ElementTrackingContextProvider>
  )
}

const Container = styled.div`
  position: relative;
`

const StyledCarousel = styled(Carousel)`
  height: calc(100vh - var(--layout-header-show-more-height, 10vh));
  min-height: 375px;
  position: 'relative';

  @media ${mediaTabletAndDown} {
    height: 328px !important;
  }

  @media ${mediaDesktopAndUp} and (max-width: ${mediaMaxMBreakpoint}px) {
    height: 460px !important;
  }

  @media ${mediaExtraLargeScreens} and (max-width: ${mediaMaxLBreakpoint}px) {
    height: 615px !important;
  }

  /* stylelint-disable */
  // using the global slick classes would be way easier
  // as the slider is used on multiple pages, used-styles would
  // detect that this styles might be used on those other pages as well
  // getting rid of the global classes solves this and will ensure to not
  // push those css files useless to those pages
  // This is less readable but good for performance. As the slick slider is
  // considered legacy and should be removed as soon as possible, this should be fine.
  & > div:first-child, /* .slick-list */
  & > div:first-child > div, /* .slick-track */
  & > div:first-child > div > div, /* .slick-slide */
  & > div:first-child > div > div div /* .slick-slide div */ {
    height: 100% !important;
  }
`

const HeroSlider = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const isClientSide = useDisplayBrowserState()

  const heroSliderPlacements = useBrandOverviewPageStore(
    store => store.heroSliderPlacements || { placements: [], name: '' },
  )

  const handleDiscoverMoreClick = useCallback(() => {
    containerRef.current &&
      animateScroll.scrollTo(containerRef.current.offsetHeight + 100)
  }, [containerRef])

  if (!heroSliderPlacements.placements.length) {
    return (
      <SectionTrackingContextProvider>
        <ElementTrackingContextProvider indexInSection={0}>
          <TrackBASMonitoring
            creative={'bas_brand_slider' as TrackingPromotionCreative}
          />
        </ElementTrackingContextProvider>
      </SectionTrackingContextProvider>
    )
  }

  // Render one or all images
  const upperLimit = isClientSide ? heroSliderPlacements.placements.length : 1
  const visibleSlides = heroSliderPlacements.placements.slice(0, upperLimit)

  return (
    <div className="container-fluid container-fluid-1920">
      <Container ref={containerRef}>
        <SectionTrackingContextProvider>
          <StyledCarousel
            withCustomDots
            autoplay
            arrows={false}
            autoplaySpeed={4000}
            infinite
            draggable={false}
          >
            {visibleSlides.map((placement, index) => (
              <SlideImage
                key={placement.id}
                placement={placement}
                campaignName={heroSliderPlacements.name}
                index={index}
              />
            ))}
          </StyledCarousel>
        </SectionTrackingContextProvider>
        <DiscoverMore onClick={handleDiscoverMoreClick} />
      </Container>
    </div>
  )
}

export default HeroSlider
